import React from "react";
import { withTranslation } from "react-i18next";
import { ONBOARDING_BOOK_A_CALL, ONBOARDING_PRODUCTS } from "../constants";
import i18n from "../i18n";
import Modal from "../parts/modal-new";
import { RillContext } from "../rill/RillReact/share/base";
import { isValidHex } from "../utils/colors";
import {
  instagramLinkFor,
  isValidInstagramHandle,
  normalizeInstagramHandle,
} from "../utils/instagram";
import {
  ATTRIBUTION_FIELDS,
  captureAttribution,
} from "../utils/signup-attribution";
import { setAppliedMode } from "../utils/theme-helpers";
import DailyDashboardPreview from "./dashboard/settings/DailyDashboardPreview";
import LogoOverlayComponent from "./dashboard/settings/LogoOverlayComponent";
import OnboardingFooter from "./onboarding/OnboardingFooter";
import OnboardingTabBar from "./onboarding/OnboardingTabBar";
import BuildingAppStep from "./onboarding/steps/BuildingAppStep";
import StepAboutYou from "./onboarding/steps/StepAboutYou";
import StepBranding from "./onboarding/steps/StepBranding";
import StepExerciseLibrary from "./onboarding/steps/StepExerciseLibrary";
import StepLogoSplash from "./onboarding/steps/StepLogoSplash";
import StepPreferences from "./onboarding/steps/StepPreferences";
import StepProducts from "./onboarding/steps/StepProducts";
import StepProfile from "./onboarding/steps/StepProfile";
import WebAppPreview from "./onboarding/WebAppPreview";
import {
  LAST_INTERACTIVE_STEP,
  PRODUCTS_BY_SEGMENT,
  STEPS_WITH_SKIP,
  WIZARD_STEPS,
} from "./onboarding/wizard-config";

const FORM_ID = "onboarding-wizard-form";
const EMAIL_REG =
  /^[^\s@,;:<>()[\]\\"]+@[^\s@,;:<>()[\]\\"]+\.[^\s@,;:<>()[\]\\"]+$/;

const showCoachModal = (t, email, onClose) => (
  <Modal title={t("signupCoach.modalTitle")} onClose={onClose}>
    <form
      method="POST"
      action="/signup-coach-existing"
      data-noreset
      className="p-8 pt-6"
    >
      <input type="hidden" name="existing" defaultValue={email} />

      <div className="chip info mb-8">
        <span>{t("signupCoach.modalChip")}</span>
      </div>

      <label htmlFor="comment">{t("signupCoach.messageLabel")}</label>
      <textarea
        className="mb-10"
        required
        name="comment"
        placeholder={t("signupCoach.messagePlaceholder")}
        defaultValue={t("signupCoach.messageDefault", { email })}
        rows={4}
      />

      <button data-button-type="primary" type="submit" className="ml-auto">
        {t("signupCoach.sendMessage")}
      </button>
    </form>
  </Modal>
);

class OnboardingWizard extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      step: 1,
      // Profile
      first_name: "",
      last_name: "",
      email: context?.locals?.query?.userEmail || "",
      businessName: "",
      password: "",
      confirmPassword: "",
      avatar: "",
      agreeTOS: false,
      validCaptcha: false,
      recaptchaTheme: "dark",
      errors: {},
      checkingEmail: false,
      emailTaken: false,
      // Segmentation + products
      segment: "",
      products: [],
      // Preferences
      units: "metric",
      language: i18n.language || "en",
      startingCalendarDay: 0,
      // Branding
      appTheme: "dark",
      webAppTheme: "dark",
      appMainColor: "#5bc500",
      appMainColorErrorValidation: false,
      appSecondaryTextColor: "black",
      instagramHandle: "",
      // Logo & splash
      logoImg: {},
      formLogoImg: {},
      mobileSplashImg: {},
      formMobileSplashImg: {},
      mobileSplashBg: {},
      formMobileSplashBg: {},
      isSplashImgTinted: false,
      whiteBg: false,
      splashOverlay: true,
      splashOverlayColor: "#000000",
      splashOverlayOpacity: 50,
      logoChanged: false,
      mobileSplashChanged: false,
      mobileSplashBgChanged: false,
      // Exercise library — selected starting-library preset ids (step 7)
      selectedPresets: [],
      viewportWidth: null,
      // Facebook-ad attribution (fbclid/utm/_fbp) captured on mount, forwarded
      // as hidden fields so the backend can track ad-sourced signups.
      attribution: {},
    };

    [
      "next",
      "back",
      "goToStep",
      "skip",
      "provision",
      "handleInputChange",
      "selectFile",
      "handleAvatarChange",
      "toggleTOS",
      "handleCaptcha",
      "selectSegment",
      "toggleProduct",
      "bookACall",
      "handleLanguageChange",
      "handleUnitsChange",
      "handleDayChange",
      "handleThemeChange",
      "handleWebThemeChange",
      "handleColorChange",
      "handleTextColorChange",
      "handleInstagramChange",
      "handleBrandingChange",
      "togglePreset",
      "updateDimensions",
      "closeExistingCoachModal",
    ].forEach((fn) => {
      this[fn] = this[fn].bind(this);
    });
  }

  componentDidMount() {
    this.setState({ viewportWidth: window.innerWidth });
    window.addEventListener("resize", this.updateDimensions);

    // Capture Facebook-ad attribution (fbclid/utm/_fbp) from the landing URL and
    // persist it across the wizard; forwarded as hidden fields at submit.
    this.setState({ attribution: captureAttribution() });

    const htmlTag = document.getElementsByTagName("html")[0];
    if (htmlTag?.attributes?.["data-theme"]?.value === "light") {
      this.setState({ recaptchaTheme: "light" });
    }

    // FirstPromoter referral tracking (preserved from the old signup flow).
    ((w) => {
      w.fpr =
        w.fpr ||
        ((...args) => {
          w.fpr.q = w.fpr.q || [];
          w.fpr.q[args[0] === "set" ? "unshift" : "push"](args);
        });
    })(window);
    fpr("init", { cid: "9g2bue8j" });
    fpr("click");
    const script = document.createElement("script");
    script.src = "https://cdn.firstpromoter.com/fpr.js";
    script.async = true;
    document.head.appendChild(script);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateDimensions);
  }

  updateDimensions() {
    this.setState({ viewportWidth: window.innerWidth });
  }

  // -- Navigation ----------------------------------------------------------
  async next() {
    const { step } = this.state;
    if (step === 1) {
      const validation = this.validateProfile();
      if (validation !== "success") {
        this.setState({ errors: validation });
        return;
      }
      this.setState({ errors: {} });

      this.setState({ checkingEmail: true });
      const taken = await this.checkEmailTaken(this.state.email.trim());
      this.setState({ checkingEmail: false });
      if (taken) {
        this.setState({ emailTaken: true });
        return;
      }
    }
    if (step < LAST_INTERACTIVE_STEP) {
      this.setState({ step: step + 1 });
    }
  }

  // Fails open (treats network/parse errors as "not taken") — the register-coach
  // POST at the final step remains the authoritative uniqueness check.
  async checkEmailTaken(email) {
    try {
      const res = await fetch(
        `/signup-coach/email-status/${encodeURIComponent(email)}`,
        { credentials: "same-origin" }
      );
      const data = await res.json();
      return !!data.taken;
    } catch {
      return false;
    }
  }

  closeExistingCoachModal() {
    this.setState({ emailTaken: false });
  }

  back() {
    this.setState((prev) => ({ step: Math.max(1, prev.step - 1) }));
  }

  goToStep(id) {
    if (id < this.state.step) this.setState({ step: id });
  }

  skip() {
    this.setState((prev) => ({
      step: Math.min(LAST_INTERACTIVE_STEP, prev.step + 1),
    }));
  }

  validateProfile() {
    const { t } = this.props;
    const s = this.state;
    if (s.first_name.length <= 1)
      return { first_name: t("signupCoach.errorFirstName") };
    if (s.last_name.length <= 1)
      return { last_name: t("signupCoach.errorLastName") };
    // Rejects what the API's Joi email rule rejects most often (commas,
    // spaces, …) here, instead of after the whole wizard is filled in.
    if (!EMAIL_REG.test(s.email.trim()))
      return { email: t("signupCoach.errorEmail") };
    if (s.password.length <= 5)
      return { password: t("signupCoach.errorPasswordLength") };
    if (s.confirmPassword !== s.password)
      return { confirmPassword: t("signupCoach.errorPasswordsMatch") };
    if (!s.agreeTOS) return { agreeTOS: t("signupCoach.errorAgreeTOS") };
    if (!s.validCaptcha) return { recaptcha: t("signupCoach.errorRecaptcha") };
    return "success";
  }

  provision() {
    const form = document.getElementById(FORM_ID);
    if (form) form.submit();
  }

  // -- Profile -------------------------------------------------------------
  handleInputChange(e) {
    e.persist();
    this.setState({ [e.target.name]: e.target.value });
  }

  selectFile(e, edit) {
    e.preventDefault();
    e.stopPropagation();
    if (this.state.avatar === "" || edit) {
      document.getElementById("avatar-input").click();
    } else {
      this.setState({ avatar: "" }, () => {
        document.getElementById("avatar-input").value = "";
      });
    }
  }

  handleAvatarChange(e) {
    e.persist();
    if (typeof e.target.files[0] === "object") {
      this.setState({ avatar: e.target.files[0] }, () => {
        const reader = new FileReader();
        const img = document.getElementById("avatar");
        reader.onload = (event) => {
          if (img) img.src = event.target.result;
        };
        reader.readAsDataURL(e.target.files[0]);
      });
    }
  }

  toggleTOS() {
    this.setState((prev) => ({ agreeTOS: !prev.agreeTOS }));
  }

  handleCaptcha(value) {
    this.setState({ validCaptcha: !!value });
  }

  // -- Segmentation + products --------------------------------------------
  selectSegment(segment) {
    // Pre-select the recommended self-serve products available to the segment.
    const groups = PRODUCTS_BY_SEGMENT[segment] || { coaching: [], gym: [] };
    const recommended = [...groups.coaching, ...groups.gym].filter((p) =>
      [ONBOARDING_PRODUCTS.PULL, ONBOARDING_PRODUCTS.PUSH].includes(p)
    );
    this.setState({ segment, products: recommended });
  }

  toggleProduct(product) {
    this.setState((prev) => {
      const gymProducts = new Set(
        (PRODUCTS_BY_SEGMENT[prev.segment] || { gym: [] }).gym
      );
      const isGymProduct = gymProducts.has(product);
      if (prev.products.includes(product)) {
        return { products: prev.products.filter((p) => p !== product) };
      }
      const next = isGymProduct
        ? prev.products.filter((p) => !gymProducts.has(p))
        : prev.products;
      return { products: [...next, product] };
    });
  }

  bookACall(product) {
    const url = ONBOARDING_BOOK_A_CALL[product];
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    // Record the interest even if they only book a call.
    this.setState((prev) => {
      if (prev.products.includes(product)) return { products: prev.products };
      const gymProducts = new Set(
        (PRODUCTS_BY_SEGMENT[prev.segment] || { gym: [] }).gym
      );
      const isGymProduct = gymProducts.has(product);
      const next = isGymProduct
        ? prev.products.filter((p) => !gymProducts.has(p))
        : prev.products;
      return { products: [...next, product] };
    });
  }

  // -- Preferences ---------------------------------------------------------
  handleLanguageChange(lang) {
    i18n.changeLanguage(lang);
    this.setState({ language: lang });
  }

  handleUnitsChange(units) {
    this.setState({ units });
  }

  handleDayChange(value) {
    this.setState({ startingCalendarDay: value });
  }

  // -- Branding ------------------------------------------------------------
  handleThemeChange(name, value) {
    this.setState({ [name]: value });
  }

  handleWebThemeChange(name, value) {
    // Web theme toggles the whole app theme live (matches prod), so the webapp
    // preview and the wizard chrome reflect the chosen theme.
    this.context.locals.theme = value;
    setAppliedMode(value);
    this.setState({ [name]: value });
  }

  handleColorChange(evt) {
    const target = evt.target || evt;
    const value = target.value.trim();
    this.setState({
      appMainColor: value,
      appMainColorErrorValidation: !isValidHex(value),
    });
  }

  handleTextColorChange(name, value) {
    this.setState({ [name]: value });
  }

  handleInstagramChange(evt) {
    this.setState({
      instagramHandle: normalizeInstagramHandle(evt.target.value),
    });
  }

  // Unified change handler for the ImageSelector + toggles on Logo & Splash
  // (mirrors onboarding-branding.js handleChange).
  handleBrandingChange(event) {
    const target = event.target || event;
    const { whiteBg, splashOverlay, isSplashImgTinted } = this.state;

    switch (target.name) {
      case "whiteBg":
      case "whiteLogoBackground":
        return this.setState({ whiteBg: !whiteBg });
      case "tintSplashImg":
        return this.setState({ isSplashImgTinted: !isSplashImgTinted });
      case "splashOverlay":
        return this.setState({ splashOverlay: !splashOverlay });
      case "splashOverlayColorPicker":
      case "splashOverlayColor":
        return this.setState({ splashOverlayColor: target.value });
      case "splashOverlayOpacity":
        return this.setState({ splashOverlayOpacity: target.value });
      case "customBranding[logoImg]":
        return this.setState({ logoChanged: true });
      case "customBranding[mobileSplashImg]":
        return this.setState({
          mobileSplashChanged: true,
          temporalSplashImg: event.backgroundImage,
        });
      case "customBranding[mobileSplashBg]":
        return this.setState({
          mobileSplashBgChanged: true,
          temporalSplashBg: event.backgroundImage,
        });
      default:
        return this.setState({ [target.name]: target.value });
    }
  }

  // -- Exercise library presets --------------------------------------------
  // Multi-select: toggle a starting-library preset id on/off. The actual
  // exercises are resolved from these ids (by tag) on the server at register.
  togglePreset(id) {
    this.setState((prev) => {
      const selected = prev.selectedPresets.includes(id)
        ? prev.selectedPresets.filter((p) => p !== id)
        : [...prev.selectedPresets, id];
      return { selectedPresets: selected };
    });
  }

  // -- Render --------------------------------------------------------------
  get continueDisabled() {
    const {
      step,
      segment,
      products,
      appMainColorErrorValidation,
      instagramHandle,
      checkingEmail,
    } = this.state;
    if (checkingEmail) return true;
    if (step === LAST_INTERACTIVE_STEP) return true;
    if (step === 2) return !segment;
    if (step === 3) return products.length === 0;
    if (step === 4)
      return (
        appMainColorErrorValidation ||
        (!!instagramHandle && !isValidInstagramHandle(instagramHandle))
      );
    return false;
  }

  // Live device previews shown in the left panel for the branding step,
  // reflecting the theme/color choices the coach makes on the right.
  renderLeftPreview() {
    const {
      step,
      appTheme,
      appMainColor,
      appSecondaryTextColor,
      mobileSplashImg,
      isSplashImgTinted,
      splashOverlay,
      splashOverlayColor,
      splashOverlayOpacity,
      mobileSplashBgChanged,
      temporalSplashBg,
      temporalSplashImg,
    } = this.state;

    if (step === 4) {
      return (
        <div className="branding-preview" aria-hidden="true">
          {/* The web-app preview has no theme prop — it reads the global theme
              CSS vars, which handleWebThemeChange flips live via setAppliedMode. */}
          <div className="preview-web">
            <div className="preview-screen">
              <WebAppPreview />
            </div>
            <img alt="" src="/img/macbook-template.webp" />
          </div>
          <div className="preview-mobile">
            <div className="preview-screen">
              <DailyDashboardPreview
                theme={appTheme}
                brandingColor={appMainColor}
                isDefaultColor={false}
                secondaryTextColor={appSecondaryTextColor}
              />
            </div>
            <img alt="" src="/img/iphone-template.webp" />
          </div>
        </div>
      );
    }

    // Logo & splash step — mobile splash-screen preview reflecting the uploaded
    // splash logo / background and overlay choices (mirrors onboarding-branding.js).
    if (step === 5) {
      return (
        <div className="branding-preview" aria-hidden="true">
          <div className="preview-splash">
            <div
              className="preview-screen"
              style={{
                backgroundImage: temporalSplashBg
                  ? `url(${temporalSplashBg})`
                  : undefined,
                backgroundColor: "var(--black)",
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            >
              <LogoOverlayComponent
                opacity={splashOverlayOpacity}
                overlayColor={splashOverlayColor}
                withOverlay={splashOverlay && mobileSplashBgChanged}
                withLogo
                logo={mobileSplashImg}
                isTinted={isSplashImgTinted}
                splashLogoSrc={temporalSplashImg}
              />
            </div>
            <img alt="" src="/img/iphone-template.webp" />
          </div>
        </div>
      );
    }

    return null;
  }

  renderLeftPanel() {
    const { t } = this.props;
    const { step, segment } = this.state;
    const key = WIZARD_STEPS[step - 1].key;
    const subtitleKey =
      key === "products" && segment === "both"
        ? "leftSubtitleBoth"
        : "leftSubtitle";

    return (
      <aside className="wizard-left">
        <span className="brand-logo" aria-hidden="true" />
        <h1>{t(`onboardingWizard.${key}.leftTitle`)}</h1>
        <p>{t(`onboardingWizard.${key}.${subtitleKey}`)}</p>
        {this.renderLeftPreview()}
        <ul className="progress-dots">
          {WIZARD_STEPS.filter((s) => !s.scaffold).map((s) => (
            <li
              key={s.id}
              className={
                s.id === step ? "active" : s.id < step ? "done" : "pending"
              }
            />
          ))}
        </ul>
      </aside>
    );
  }

  renderStep() {
    const { step } = this.state;
    const mobile = this.state.viewportWidth && this.state.viewportWidth <= 960;
    const exerciseLibraryPresets =
      this.context?.locals?.exerciseLibraryPresets || [];

    return (
      <>
        <div className={step === 1 ? "" : "onboarding-step-hidden"}>
          <StepProfile
            current={1}
            state={this.state}
            onInputChange={this.handleInputChange}
            onSelectFile={this.selectFile}
            onAvatarChange={this.handleAvatarChange}
            onToggleTOS={this.toggleTOS}
            onCaptcha={this.handleCaptcha}
          />
        </div>

        <div className={step === 2 ? "" : "onboarding-step-hidden"}>
          <StepAboutYou
            current={2}
            segment={this.state.segment}
            onSelectSegment={this.selectSegment}
          />
        </div>

        <div className={step === 3 ? "" : "onboarding-step-hidden"}>
          <StepProducts
            current={3}
            segment={this.state.segment}
            products={this.state.products}
            onToggleProduct={this.toggleProduct}
            onBookACall={this.bookACall}
          />
        </div>

        <div className={step === 4 ? "" : "onboarding-step-hidden"}>
          <StepBranding
            current={4}
            state={this.state}
            mobile={mobile}
            onThemeChange={this.handleThemeChange}
            onWebThemeChange={this.handleWebThemeChange}
            onColorChange={this.handleColorChange}
            onTextColorChange={this.handleTextColorChange}
            onInstagramChange={this.handleInstagramChange}
          />
        </div>

        <div className={step === 5 ? "" : "onboarding-step-hidden"}>
          <StepLogoSplash
            current={5}
            state={this.state}
            mobile={mobile}
            onChange={this.handleBrandingChange}
          />
        </div>

        <div className={step === 6 ? "" : "onboarding-step-hidden"}>
          <StepPreferences
            current={6}
            state={this.state}
            onLanguageChange={this.handleLanguageChange}
            onUnitsChange={this.handleUnitsChange}
            onDayChange={this.handleDayChange}
          />
        </div>

        <div className={step === 7 ? "" : "onboarding-step-hidden"}>
          <StepExerciseLibrary
            current={7}
            presets={exerciseLibraryPresets}
            selectedPresets={this.state.selectedPresets}
            onTogglePreset={this.togglePreset}
            onSkip={this.skip}
          />
        </div>

        <div className={step === 8 ? "" : "onboarding-step-hidden"}>
          {step === 8 ? (
            <BuildingAppStep
              customBranding={{
                appTheme: this.state.appTheme,
                appMainColor: this.state.appMainColor,
                appSecondaryTextColor: this.state.appSecondaryTextColor,
                mobileSplashImg: this.state.mobileSplashImg,
                // Uploads aren't on S3 until provisioning, so preview the
                // local files (same sources as the step 5 preview).
                splashImgSrc: this.state.temporalSplashImg,
                splashBgSrc: this.state.temporalSplashBg,
                tintSplashImg: this.state.isSplashImgTinted,
                withOverlay:
                  this.state.splashOverlay && this.state.mobileSplashBgChanged,
                splashOverlayColor: this.state.splashOverlayColor,
                splashOverlayOpacity: this.state.splashOverlayOpacity,
              }}
              products={this.state.products}
              onProvision={this.provision}
            />
          ) : null}
        </div>
      </>
    );
  }

  render() {
    const { t } = this.props;
    const { step } = this.state;
    const isBuilding = step === LAST_INTERACTIVE_STEP;
    const existingCoach =
      this.props.flash("existingCoach") || this.state.emailTaken;

    return (
      <>
        {existingCoach &&
          showCoachModal(
            t,
            this.context.locals.query.email || this.state.email,
            this.closeExistingCoachModal
          )}
        <main className="OnboardingWizard Onboarding" data-step={step}>
          {!isBuilding && (
            <OnboardingTabBar current={step} onTabClick={this.goToStep} />
          )}

          <div className={`wizard-card ${isBuilding ? "building" : ""}`}>
            {!isBuilding && this.renderLeftPanel()}
            <section className="wizard-right">
              <form
                id={FORM_ID}
                method="POST"
                action="/signup-coach"
                encType="multipart/form-data"
                data-noreset
              >
                {/* appTheme, webAppTheme, appMainColor, appSecondaryTextColor
                    (StepBranding) and startingCalendarDay (StepPreferences)
                    are NOT duplicated here — their own step components
                    (ThemeSelector / InputColor / MultiOptionsInput / Select)
                    already render a submission-ready field with that same
                    name. A second field with the same name here would
                    duplicate it into an array on submit. */}
                {ATTRIBUTION_FIELDS.map((name) => (
                  <input
                    key={name}
                    type="hidden"
                    name={name}
                    value={this.state.attribution[name] || ""}
                    readOnly
                  />
                ))}
                {this.renderStep()}
              </form>
            </section>
          </div>

          <OnboardingFooter
            current={step}
            canGoBack={step > 1 && !isBuilding}
            canSkip={STEPS_WITH_SKIP.has(step)}
            onBack={this.back}
            onSkip={this.skip}
            onContinue={isBuilding ? this.provision : this.next}
            continueDisabled={this.continueDisabled}
            homeDisabled={isBuilding}
          />
        </main>
      </>
    );
  }
}

OnboardingWizard.contextType = RillContext;

const TranslatedOnboardingWizard = withTranslation("auth")(OnboardingWizard);

// -- Provisioning helpers (BFF) --------------------------------------------
const safeParse = (value, fallback) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

// castJSON (app.js) already turns "true"/"false" fields into booleans; accept
// the raw strings too in case a field reaches here uncast.
const toBool = (value, fallback) =>
  value == null || value === "" ? fallback : value === true || value === "true";

const Onboarding = (app) => {
  app
    .get("/signup-coach", async ({ res, isLoggedIn, flash, load }) => {
      if (isLoggedIn()) {
        flash("error", "Please log out before trying to sign up");
        return res.redirect("/dashboard");
      }
      // Public preset list + live counts for the "Pick a starting exercise
      // library" step (no auth required — see exercise-library-presets API).
      await load("exerciseLibraryPresets", {});
      res.body = <TranslatedOnboardingWizard flash={flash} />;
    })
    .get(
      "/signup-coach/email-status/:email",
      async ({ req, res, api, assert }) => {
        // rill's router never decodes path params (at either hop), so undo the
        // browser's encodeURIComponent here before forwarding the raw email on —
        // the backend's own router expects it un-encoded for the same reason.
        const email = decodeURIComponent(req.params.email || "");
        assert(email, 400, "An email address is required");
        const status = await (
          await api(`user-status/${email}`, {
            method: "GET",
          })
        ).json();
        res.body = { taken: !status.nonExistent };
      }
    )
    .post(
      "/signup-coach-existing",
      async ({ req: { body }, res, api, flash }) => {
        flash("success", "Submission successfully sent");
        const contact = {
          type: `Free Trial Coach - ${body.existing}`,
          message: body.comment,
          email: body.existing,
        };
        await api("contact", { method: "POST", body: contact });
        return res.redirect("/signup-coach");
      }
    )
    .post("/signup-coach", async ({ req, res, api, flash, assert, login }) => {
      const { body, files } = req;

      assert(body.first_name, 400, "A first name is required");
      assert(body.last_name, 400, "A last name is required");
      assert(body.email, 400, "An email address is required");
      body.email = String(body.email).trim();
      assert(body.password, 400, "A password is required");
      assert(
        body.confirmPassword === body.password,
        400,
        "Passwords must match"
      );

      const selectedPresets = safeParse(body.selectedPresets, []);
      const products = safeParse(body.onboardingProducts, []);
      const instagramHandle = normalizeInstagramHandle(body.instagramHandle);
      // Checked lifts arrive as { squat: true, ... }, like Settings.
      const collectRepMax = Object.keys(body.collectRepMax || {});

      const registerBody = {
        name: `${body.first_name} ${body.last_name}`,
        email: body.email,
        password: body.password,
        businessName: body.businessName,
        units: body.units || "metric",
        language: body.language || "en",
        startingCalendarDay: Number(body.startingCalendarDay) || 0,
        // Training app preferences (step 6). User reference is only shown when
        // PULL is picked; absent means the schema default (athlete).
        referAsClients: toBool(body.referAsClients, false),
        round: toBool(body.round, true),
        hideStrengthMetrics: toBool(body.hideStrengthMetrics, false),
        hideE1rm: toBool(body.hideE1rm, false),
        intensityMeasure: body.intensityMeasure === "rir" ? "rir" : "rpe",
        collectRepMax,
        // An empty list otherwise falls back to collecting squat/bench/deadlift.
        hideRepMaximums: collectRepMax.length === 0,
        webAppTheme: body.webAppTheme || "dark",
        onboardingSegment: body.onboardingSegment || undefined,
        onboardingProducts: products,
        customBranding: {
          appTheme: body.appTheme || "dark",
          appMainColor: body.appMainColor || "#5bc500",
          appSecondaryTextColor: body.appSecondaryTextColor || "black",
          tintSplashImg: toBool(body.tintSplashImg, false),
          whiteLogoBackground: toBool(body.whiteBg, false),
          splashOverlayColor: body.splashOverlayColor || "#000000",
          splashOverlayOpacity: Number(body.splashOverlayOpacity) || 50,
          appColorsDefault: false,
          // Optional (step 4). The link feeds the athlete app's Community icon.
          ...(isValidInstagramHandle(instagramHandle) && {
            instagramHandle,
            instagramLink: instagramLinkFor(instagramHandle),
          }),
        },
        // Starting-library preset ids; register-coach resolves these into a
        // real exerciseLibrary (exercises by tag, deduped across presets).
        selectedPresets,
        // Facebook-ad attribution (hidden fields). register-coach gates its
        // signup tracking on `fbclid` and builds the FB event from `fbc`/`fbp`.
        fbclid: body.fbclid || undefined,
        fbc: body.fbc || undefined,
        fbp: body.fbp || undefined,
        utm_source: body.utm_source || undefined,
        utm_campaign: body.utm_campaign || undefined,
        utm_medium: body.utm_medium || undefined,
        utm_content: body.utm_content || undefined,
        utm_term: body.utm_term || undefined,
      };

      try {
        await (
          await api("register-coach", {
            method: "POST",
            body: registerBody,
            files,
          })
        ).json();

        const { token, userId } = await (
          await api("login", {
            method: "POST",
            body: { email: body.email, password: body.password },
          })
        ).json();

        await login({ _id: userId, token }, { refresh: true, ttl: "1 day" });

        // fpr is a browser global; only present when this handler runs client-side.
        if (typeof fpr !== "undefined") {
          fpr("referral", { uid: userId, email: body.email });
        }
        return res.redirect("/coaching-portal");
      } catch (e) {
        if (e.code === 400 && e.message.includes("Email")) {
          flash("existingCoach", true);
        }

        flash("error", e.message);
        return res.refreshWith({ email: body.email });
      }
    });
};

export default Onboarding;
